import { type CSSInterpolation } from '@emotion/serialize'

import { getPrettyRunStatus } from 'components/services/service-utils'
import { muiTheme } from 'mui-theme'
import { PolicyResult } from 'types/api/policies/policy'
import { getLightMuiBackgroundColor } from 'utils/colors'
import { formatTimePeriodDate } from 'utils/time/date'

import type { RiskSummaryStats } from 'types/api/services/service'

/**
 * Reliability score thresholds that define the upper bound of each band.
 *
 * Bands:
 *   0  – 50        → error (red)
 *   51 – 70        → warning (yellow)
 *   71 – Infinity  → success (green, open-ended to accommodate extra credit scores above 100)
 */
const RELIABILITY_SCORE_RED_MAX = 50
const RELIABILITY_SCORE_YELLOW_MAX = 70

export const RELIABILITY_RED_BAND = {
  color: muiTheme.palette.error.light,
  max: RELIABILITY_SCORE_RED_MAX
}
export const RELIABILITY_YELLOW_BAND = {
  color: muiTheme.palette.warning.light,
  max: RELIABILITY_SCORE_YELLOW_MAX
}
export const RELIABILITY_GREEN_BAND = {
  color: muiTheme.palette.success.light,
  max: Infinity
}

/**
 * Score bands pairing each range with its MUI palette color.
 * Ordered from lowest to highest score. Each band's `max` is its upper
 * boundary (inclusive). The green band's max is Infinity to accommodate
 * extra credit scores above 100. Chart reference areas use a separate
 * maxY parameter to bound the green zone visually.
 *
 * Used by chart reference areas and background color lookups so that
 * both derive from a single source of truth.
 */
export const RELIABILITY_SCORE_BANDS = [
  RELIABILITY_RED_BAND,
  RELIABILITY_YELLOW_BAND,
  RELIABILITY_GREEN_BAND
]

export const DONUT_WIDTH = 200
export const REPORT_CHART_LOADING_BOX_HEIGHT = 360

export const RELIABILITY_REPORT_COLORS = {
  SCALABILITY: muiTheme.palette.marketingViolet650.main,
  REDUNDANCY: muiTheme.palette.orchid.dark,
  DEPENDENCIES: muiTheme.palette.marketingBlue400.main,
  RISKS: muiTheme.palette.oceanBlue.main,
  OTHER: muiTheme.palette.marketingViolet200.main,
  EXTRA_CREDIT: muiTheme.palette.orchid.light
}

export const reportTableStyles = {
  '& .MuiDataGrid-cell:focus': {
    outline: 0
  },
  '& .MuiDataGrid-columnSeparator': {
    display: 'none'
  },
  '& .MuiDataGrid-columnHeaderTitleContainerContent': {
    width: '100%',
    justifyContent: 'center'
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    whiteSpace: 'initial',
    lineHeight: 'normal',
    textAlign: 'center',
    // These styles are necessary to prevent the column header from clipping when there are more than 4 lines of text
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical'
  },
  border: 'none'
}

/**
 * EN-6602 - Styling to allow for horizontal scrolling for team reports
 */
export const PAGE_SECTION_REPORT_STYLES: CSSInterpolation = {
  display: 'block',
  flex: 'unset',
  marginLeft: '-42px', // undo some padding set by Content
  overflowX: 'auto', // only show scroll if width is excessive
  overflowY: 'hidden',
  width: 'calc(100vw - 300px)' // 207px: width of left app sidebar plus Content padding
}

export function getTeamReliabilityReportDependencyTooltipTitle(
  runStatus: PolicyResult
) {
  if (runStatus === 'FAILED') {
    return '1 or more dependencies in this service has a non-passing state'
  }

  if (runStatus === 'NEVER_RUN') {
    return 'Never tested'
  }

  return getPrettyRunStatus(runStatus)
}

export function getResultBySeverity(
  resultSet: Set<PolicyResult>
): PolicyResult {
  // Defines the order of severity for policy results
  // The first matching result in this order will be returned
  const severityOrder: PolicyResult[] = [
    PolicyResult.FAILED,
    PolicyResult.EXPIRED,
    PolicyResult.NEVER_RUN,
    PolicyResult.IGNORED,
    PolicyResult.PASSED
  ]

  for (const result of severityOrder) {
    if (resultSet.has(result)) {
      return result
    }
  }

  // Default case if no results match the severity order
  return PolicyResult.IGNORED
}

export function getRiskDelta(riskSummaryStats: RiskSummaryStats) {
  const { atRisk, previousIntervalAtRisk } = riskSummaryStats

  return atRisk - previousIntervalAtRisk
}

export function buildReportPdfFilename(owner: string, label: string) {
  return `${owner}-${label}-${formatTimePeriodDate(new Date())}.pdf`
}

export function getBackgroundColorFromReliabilityScore(
  reliabilityScore: number
) {
  const band =
    RELIABILITY_SCORE_BANDS.find((b) => reliabilityScore <= b.max) ??
    RELIABILITY_GREEN_BAND

  return getLightMuiBackgroundColor(band.color)
}
